import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner, Table } from '@/components/ui';
import { vehicleTransferService, type SettingsPerson } from '../../../services/vehicleTransferService';
import { serviceAreaLabel } from '../utils/equipmentDisplay';
import TransferPersonPicker, { type PickedPerson } from './TransferPersonPicker';

interface Props {
  show: boolean;
  onHide: () => void;
}

interface AreaRow {
  serviceAreaId: number;
  label: string;
  opsManager: PickedPerson | null;
  fleetApprover: PickedPerson | null;
}

const picked = (p: SettingsPerson | null): PickedPerson | null => (p ? { id: p.id, name: `${p.name} ${p.lastName}`.trim() } : null);

/**
 * Transfer Settings (admins): who approves and who is told.
 * - Default fleet approver (Dave Ratch), used where an area has none of its own.
 * - Per service area: its Ops Manager and, optionally, its own fleet approver.
 * - Copied (Gord) and Finance ("Please forward to", plus Skyhawk) email lists.
 */
const TransferSettingsModal: React.FC<Props> = ({ show, onHide }) => {
  const [loading, setLoading] = useState(true);
  const [fleet, setFleet] = useState<PickedPerson | null>(null);
  const [financeEmails, setFinanceEmails] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [areas, setAreas] = useState<AreaRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!show) return;
    setLoading(true);
    setError(null);
    setSaved(false);
    vehicleTransferService
      .settings()
      .then((s) => {
        setFleet(picked(s.fleetApprover));
        setFinanceEmails(s.financeEmails.join('\n'));
        setCcEmails(s.ccEmails.join('\n'));
        setAreas(
          s.areas.map((a) => ({
            serviceAreaId: a.serviceAreaId,
            label: serviceAreaLabel(a),
            opsManager: picked(a.opsManager),
            fleetApprover: picked(a.fleetApprover),
          }))
        );
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load the settings. Close and try again.'))
      .finally(() => setLoading(false));
  }, [show]);

  const changed = () => setSaved(false);
  const setArea = (id: number, patch: Partial<AreaRow>) => {
    setAreas((rows) => rows.map((r) => (r.serviceAreaId === id ? { ...r, ...patch } : r)));
    changed();
  };

  const save = async () => {
    if (!fleet) {
      setError('Choose the default fleet approver.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await vehicleTransferService.updateSettings({
        fleetApproverUserId: fleet.id,
        financeEmails,
        ccEmails,
        areas: areas.map((a) => ({
          serviceAreaId: a.serviceAreaId,
          opsManagerUserId: a.opsManager?.id ?? null,
          fleetApproverUserId: a.fleetApprover?.id ?? null,
        })),
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Transfer Settings</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="tw-py-6 tw-text-center">
            <Spinner role="status" />
          </div>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-5">
            <div className="tw-grid tw-gap-5 md:tw-grid-cols-3">
              <Form.Group controlId="transfer-settings-fleet">
                <Form.Label>Default Fleet Approver</Form.Label>
                <TransferPersonPicker id="transfer-settings-fleet" value={fleet} onChange={(p) => (setFleet(p), changed())} />
                <Form.Text className="tw-text-content-muted">Approves transfers into any area without its own fleet approver.</Form.Text>
              </Form.Group>
              <Form.Group controlId="transfer-settings-cc">
                <Form.Label>Copied On</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={ccEmails}
                  placeholder="One email address per line"
                  onChange={(e) => (setCcEmails(e.target.value), changed())}
                />
                <Form.Text className="tw-text-content-muted">Get every approval request and approved record, without approving.</Form.Text>
              </Form.Group>
              <Form.Group controlId="transfer-settings-finance">
                <Form.Label>Finance and Skyhawk</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={financeEmails}
                  placeholder="One email address per line"
                  onChange={(e) => (setFinanceEmails(e.target.value), changed())}
                />
                <Form.Text className="tw-text-content-muted">
                  Get the approved record to apply in Sage and update Skyhawk, with everyone whose role can complete transfers.
                </Form.Text>
              </Form.Group>
            </div>

            <div>
              <h3 className="tw-mb-1 tw-text-sm tw-font-semibold">Approvers by Service Area</h3>
              <p className="tw-mb-2 tw-text-xs tw-text-content-muted">
                The Ops Manager approves transfers out of and into their area. With none set, WRAP uses the Operations Manager whose
                default foreman area is in it. A fleet approver set here replaces the default for transfers into that area.
              </p>
              <Table responsive className="tw-mb-0">
                <thead>
                  <tr>
                    <th>Service Area</th>
                    <th>Ops Manager</th>
                    <th>Fleet Approver</th>
                  </tr>
                </thead>
                <tbody>
                  {areas.map((a) => (
                    <tr key={a.serviceAreaId}>
                      <td className="tw-whitespace-nowrap tw-align-top">{a.label}</td>
                      <td className="tw-min-w-[14rem] tw-align-top">
                        <TransferPersonPicker
                          id={`area-${a.serviceAreaId}-ops`}
                          value={a.opsManager}
                          onChange={(p) => setArea(a.serviceAreaId, { opsManager: p })}
                        />
                      </td>
                      <td className="tw-min-w-[14rem] tw-align-top">
                        <TransferPersonPicker
                          id={`area-${a.serviceAreaId}-fleet`}
                          value={a.fleetApprover}
                          onChange={(p) => setArea(a.serviceAreaId, { fleetApprover: p })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {error && <Alert variant="danger" className="tw-mb-0">{error}</Alert>}
            {saved && (
              <p role="status" className="tw-mb-0 tw-text-sm tw-text-success">
                Saved. New requests use these settings; reassign a pending one from the transfer itself.
              </p>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Close
        </Button>
        <Button onClick={save} disabled={saving || loading}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TransferSettingsModal;
